// client/src/pages/Moonbeams.js
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import MoonbeamCard from '../components/moonbeams/MoonbeamCard.js';
import { useAuth } from '../hooks/useAuth.js';
import { useSettings } from '../hooks/useSettings.js';
import api from '../services/api.js';

/* ─── Icons ────────────────────────────────────────── */
const FilterIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
  </svg>
);

/* ─── Vintage Search Bar ───────────────────────────── */
function VintageSearch({ value, onChange, placeholder }) {
  return (
    <div className="relative group">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gold-dark dark:text-gold text-sm opacity-60 font-display pointer-events-none">✦</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full pl-9 pr-10 py-3 rounded-xl
          bg-white dark:bg-royal-800/60
          border-2 border-gold/30 dark:border-gold/20
          text-royal-900 dark:text-royal-100
          placeholder:text-royal-400 dark:placeholder:text-royal-300
          placeholder:italic placeholder:font-body
          font-body text-base
          focus:outline-none focus:border-gold/60 dark:focus:border-gold/50
          focus:shadow-lg focus:shadow-gold/5
          transition-all"
      />
      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
        <svg className="w-5 h-5 text-gold-dark dark:text-gold opacity-50 group-focus-within:opacity-80 transition-opacity" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
          <circle cx="11" cy="11" r="7" />
          <path strokeLinecap="round" d="M16.5 16.5L21 21" />
        </svg>
      </div>
      <span className="absolute right-10 top-1/2 -translate-y-1/2 text-gold-dark dark:text-gold text-sm opacity-60 font-display pointer-events-none">✦</span>
    </div>
  );
}

/* ─── Pagination ───────────────────────────────────── */
function Pagination({ page, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;

  const pages = [];
  const maxVisible = 5;
  let start = Math.max(1, page - Math.floor(maxVisible / 2));
  let end = Math.min(totalPages, start + maxVisible - 1);
  if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
  for (let i = start; i <= end; i++) pages.push(i);

  const btnBase = 'px-3 py-2.5 text-base rounded-lg border transition-colors';
  const btnNormal = 'border-royal-300 dark:border-royal-600 text-royal-600 dark:text-royal-200 hover:border-gold/50 hover:text-gold';
  const btnActive = 'border-gold bg-gold/20 text-gold-dark dark:text-gold';
  const btnDisabled = 'disabled:opacity-60 disabled:cursor-not-allowed';

  return (
    <div className="flex items-center justify-center gap-2 mt-10">
      <button onClick={() => onPageChange(page - 1)} disabled={page <= 1} className={`${btnBase} ${btnNormal} ${btnDisabled}`}>
        ← Prev
      </button>
      <div className="hidden sm:flex gap-1">
        {start > 1 && (
          <>
            <button onClick={() => onPageChange(1)} className={`${btnBase} ${btnNormal}`}>1</button>
            {start > 2 && <span className="px-2 py-2.5 text-royal-400 dark:text-royal-300">...</span>}
          </>
        )}
        {pages.map((p) => (
          <button key={p} onClick={() => onPageChange(p)} className={`${btnBase} ${p === page ? btnActive : btnNormal}`}>
            {p}
          </button>
        ))}
        {end < totalPages && (
          <>
            {end < totalPages - 1 && <span className="px-2 py-2.5 text-royal-400 dark:text-royal-300">...</span>}
            <button onClick={() => onPageChange(totalPages)} className={`${btnBase} ${btnNormal}`}>{totalPages}</button>
          </>
        )}
      </div>
      <span className="sm:hidden text-royal-500 dark:text-royal-300 text-base px-3">{page} / {totalPages}</span>
      <button onClick={() => onPageChange(page + 1)} disabled={page >= totalPages} className={`${btnBase} ${btnNormal} ${btnDisabled}`}>
        Next →
      </button>
    </div>
  );
}

/* ─── Tab Button ───────────────────────────────────── */
function TabButton({ active, onClick, children, count }) {
  return (
    <button
      onClick={onClick}
      className={`px-6 py-2.5 text-base font-medium rounded-lg transition-all ${
        active
          ? 'bg-gold text-royal-900 shadow-lg'
          : 'bg-white dark:bg-royal-800/50 text-royal-600 dark:text-royal-200 border border-royal-300 dark:border-royal-600 hover:border-gold/50 hover:text-gold'
      }`}
    >
      {children}
      {typeof count === 'number' && (
        <span className={`ml-2 text-sm ${active ? 'text-royal-900/70' : 'text-royal-400 dark:text-royal-300'}`}>
          ({count})
        </span>
      )}
    </button>
  );
}

/* ─── Sidebar Category Item ────────────────────────── */
function SidebarItem({ icon, label, active, count, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-base transition-colors text-left ${
        active
          ? 'bg-gold/15 text-gold-dark dark:text-gold'
          : 'text-royal-700 dark:text-royal-200 hover:bg-gold/5 dark:hover:bg-royal-800/40 hover:text-gold-dark dark:hover:text-gold'
      }`}
    >
      <span className="text-lg w-6 text-center">{icon}</span>
      <span className="font-body flex-1 truncate">{label}</span>
      {typeof count === 'number' && (
        <span className={`text-sm tabular-nums ${active ? 'text-gold-dark/70 dark:text-gold/70' : 'text-royal-400'}`}>
          {count}
        </span>
      )}
    </button>
  );
}

/* ─── Category icon mapping ────────────────────────── */
const CATEGORY_ICONS = {
  'birthday': '🎂', 'thank you': '💛', 'love': '💌', 'get well': '🌷',
  'congratulations': '🎉', 'friendship': '🤝', 'holiday': '🎄',
  'just because': '✨', 'sympathy': '🕊️', 'anniversary': '💍',
  'new baby': '👶', 'graduation': '🎓', 'wedding': '💒',
  'valentine': '💕', 'st. patrick': '☘️', 'easter': '🐣',
  'mother': '💐', 'father': '👔', 'fourth': '🎆', 'july': '🎆',
  'halloween': '🎃', 'thanksgiving': '🦃', 'hanukkah': '🕎',
  'christmas': '🎄', 'new year': '🥂', 'baby shower': '🍼',
  'bridal': '👰', 'memorial': '🇺🇸',
};

function getCategoryIcon(name) {
  const lower = (name || '').toLowerCase();
  for (const [key, icon] of Object.entries(CATEGORY_ICONS)) {
    if (lower.includes(key)) return icon;
  }
  return '🌟';
}

/* ─── Debounce hook ────────────────────────────────── */
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debouncedValue;
}

/* ═══════════════════════════════════════════════════════
   Main Moonbeams component
   ═══════════════════════════════════════════════════════ */
export default function Moonbeams() {
  const { user } = useAuth();
  const { settings } = useSettings();
  const [searchParams, setSearchParams] = useSearchParams();
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [counts, setCounts] = useState({ personal: 0, business: 0 });

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const [specialAccessTemplates, setSpecialAccessTemplates] = useState([]);
  const [specialAccessLoading, setSpecialAccessLoading] = useState(false);
  const [showSpecialAccess, setShowSpecialAccess] = useState(false);

  // Debounce search so we don't hit the server on every keystroke
  const debouncedSearch = useDebounce(searchQuery, 350);

  const activeTab = searchParams.get('tab') || 'personal';
  const activeCategory = searchParams.get('category') || '';
  const activeIndustry = searchParams.get('industry') || '';
  const activeFreeFilter = searchParams.get('free') || '';
  const currentPage = parseInt(searchParams.get('page')) || 1;

  const userType = user?.usertype || 0;
  const isBusinessUser = userType >= 4;
  const isFreeUser = user && !user.tier?.ispaid;
  const isPaidUser = user?.tier?.ispaid;
  const isAdmin = user?.isadmin || false;

  // Business tab visibility: requires business tier AND setting enabled (admins always see it)
  const businessTemplatesEnabled = settings.business_templates_enabled !== false; // default true if not loaded yet
  const showBusinessTab = isBusinessUser && (businessTemplatesEnabled || isAdmin);

  const updateParams = useCallback((updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) newParams.set(key, value);
      else newParams.delete(key);
    });
    if (!('page' in updates)) newParams.delete('page');
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  const switchTab = (tab) => {
    setSearchParams({ tab });
    setShowSpecialAccess(false);
  };

  const selectCategory = (categoryId) => {
    updateParams({ category: categoryId ? String(categoryId) : '' });
    setSidebarOpen(false);
  };

  useEffect(() => {
    if (user) {
      setSpecialAccessLoading(true);
      api.get('/templates/special-access')
        .then((d) => setSpecialAccessTemplates(d.templates || []))
        .catch(() => setSpecialAccessTemplates([]))
        .finally(() => setSpecialAccessLoading(false));
    }
  }, [user]);

  useEffect(() => {
    const params = showBusinessTab ? '?usertype=4' : '';
    api.get(`/templates/categories${params}`, false)
      .then((d) => setCategories(d.categories || []))
      .catch(() => {});
  }, [showBusinessTab]);

  useEffect(() => {
    if (showBusinessTab) {
      api.get('/templates/industries', false)
        .then((d) => setIndustries(d.industries || []))
        .catch(() => {});
    }
  }, [showBusinessTab]);

  // Reset to page 1 when search changes
  useEffect(() => {
    if (debouncedSearch !== undefined) {
      updateParams({ page: '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (showBusinessTab) params.set('usertype', '4');
    if (activeCategory) params.set('category', activeCategory);
    if (activeIndustry && showBusinessTab && activeTab === 'business') params.set('industry', activeIndustry);
    params.set('page', currentPage.toString());
    params.set('limit', '21');
    if (debouncedSearch.trim()) params.set('search', debouncedSearch.trim());

    // Pass auth=true so optionalAuthMiddleware can identify admin users
    // and include admin-only templates in the results
    api.get(`/templates?${params.toString()}`)
      .then((d) => {
        let filtered = d.templates || [];
        if (showBusinessTab) {
          filtered = activeTab === 'business'
            ? filtered.filter(t => t.isbusiness)
            : filtered.filter(t => !t.isbusiness);
        }
        if (activeFreeFilter === '1') filtered = filtered.filter(t => t.isfree);

        setTemplates(filtered);
        const all = d.templates || [];
        setCounts({
          personal: all.filter(t => !t.isbusiness).length,
          business: all.filter(t => t.isbusiness).length,
        });
        setPagination({
          page: d.pagination?.page || 1,
          limit: d.pagination?.limit || 21,
          total: d.pagination?.total || filtered.length,
          totalPages: d.pagination?.totalPages || 1,
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [activeCategory, activeIndustry, activeFreeFilter, currentPage, showBusinessTab, activeTab, debouncedSearch]);

  const selectedIndustryName = industries.find(i => String(i.industrytypeid) === activeIndustry)?.industryname || 'All Industries';

  const handlePageChange = (newPage) => {
    updateParams({ page: newPage.toString() });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const personalCategories = categories.filter(c => !c.isbusiness);
  const activeCategoryName = categories.find(c => String(c.categoryid) === activeCategory)?.categoryname;

  // Search is now handled server-side via the search query param
  const filteredTemplates = templates;

  // Group special access templates by accessname
  const specialAccessGroups = useMemo(() => {
    if (!specialAccessTemplates.length) return [];

    const groupMap = new Map();
    for (const t of specialAccessTemplates) {
      const groupKey = t.accessname || 'Special Collection';
      if (!groupMap.has(groupKey)) {
        groupMap.set(groupKey, {
          name: groupKey,
          // Use the earliest expiry across templates in this group
          expiresat: t.expiresat || null,
          templates: [],
        });
      }
      const group = groupMap.get(groupKey);
      group.templates.push(t);
      // Track the earliest expiry for the group
      if (t.expiresat && (!group.expiresat || new Date(t.expiresat) < new Date(group.expiresat))) {
        group.expiresat = t.expiresat;
      }
    }

    return [...groupMap.values()];
  }, [specialAccessTemplates]);

  return (
    <div className="min-h-screen">

      {/* ── Hero Banner — ALWAYS dark ─────────────────── */}
      <div className="relative overflow-hidden bg-royal-900">
        <div className="absolute inset-0 bg-hero-gradient opacity-80"></div>
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 30% 20%, rgba(212,175,55,0.08) 0%, transparent 50%), radial-gradient(ellipse at 70% 80%, rgba(244,208,63,0.05) 0%, transparent 50%)'
        }}></div>
        <div className="relative max-w-3xl mx-auto px-4 pt-12 sm:pt-16 pb-20 sm:pb-24 text-center">
          <h1 className="font-display text-3xl sm:text-5xl text-gold mb-3 sm:mb-4">Moonbeams</h1>
          <div className="flex items-center justify-center gap-4 mb-3 sm:mb-4">
            <div className="h-px w-12 bg-gradient-to-r from-transparent via-gold to-transparent"></div>
            <span className="text-gold text-lg">✦</span>
            <div className="h-px w-12 bg-gradient-to-r from-transparent via-gold to-transparent"></div>
          </div>
          <p className="text-royal-100 text-base sm:text-lg italic leading-relaxed max-w-md mx-auto">
            Digital cards to share with friends — choose one, make it yours, and send a little magic.
          </p>
        </div>

        {/* Wave curve */}
        <div className="absolute -bottom-px left-0 right-0">
          <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full block" preserveAspectRatio="none">
            <path
              d="M0 80L60 72C120 64 240 48 360 40C480 32 600 32 720 36C840 40 960 48 1080 52C1200 56 1320 56 1380 56L1440 56V80H1380C1320 80 1200 80 1080 80C960 80 840 80 720 80C600 80 480 80 360 80C240 80 120 80 60 80H0Z"
              className="fill-cream dark:fill-bg-dark"
            />
          </svg>
        </div>
      </div>

      {/* ── Body: sidebar + content ───────────────────── */}
      <div className="bg-cream dark:bg-bg-dark transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">

          {/* Tabs row — sits above the sidebar+grid layout */}
          <div className="flex flex-wrap items-center gap-3 py-6">
            {/* Mobile: sidebar toggle */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden flex items-center gap-2 px-3 py-2.5 rounded-lg border border-royal-300 dark:border-royal-600 text-royal-600 dark:text-royal-200 hover:border-gold/50 hover:text-gold transition-colors text-sm"
            >
              <FilterIcon />
              Categories
            </button>
            {activeCategoryName && (
              <span className="lg:hidden text-sm text-gold-dark dark:text-gold font-medium truncate">{activeCategoryName}</span>
            )}

            {showBusinessTab && (
              <>
                <TabButton active={activeTab === 'personal' && !showSpecialAccess} onClick={() => switchTab('personal')} count={counts.personal}>
                  Personal
                </TabButton>
                <TabButton active={activeTab === 'business' && !showSpecialAccess} onClick={() => switchTab('business')} count={counts.business}>
                  Business
                </TabButton>
              </>
            )}
            {user && (
              <TabButton active={showSpecialAccess} onClick={() => setShowSpecialAccess(!showSpecialAccess)} count={specialAccessTemplates.length > 0 ? specialAccessTemplates.length : undefined}>
                Special Access
              </TabButton>
            )}
            {isFreeUser && !isPaidUser && !showSpecialAccess && (
              <button
                onClick={() => updateParams({ free: activeFreeFilter === '1' ? '' : '1' })}
                className={`px-6 py-2.5 text-base font-medium rounded-lg transition-all ${
                  activeFreeFilter === '1'
                    ? 'bg-green-600 text-white shadow-lg'
                    : 'bg-white dark:bg-royal-800/50 text-green-600 dark:text-green-400 border border-green-400 dark:border-green-600 hover:border-green-500 hover:bg-green-50 dark:hover:bg-green-900/20'
                }`}
              >
                ✨ Free Only
              </button>
            )}
          </div>

          {/* Industry filter — business tab only */}
          {showBusinessTab && activeTab === 'business' && !showSpecialAccess && industries.length > 0 && (
            <div className="flex items-center gap-3 mb-6">
              <label className="text-royal-600 dark:text-royal-200 text-base whitespace-nowrap">Industry:</label>
              <select
                value={activeIndustry}
                onChange={(e) => updateParams({ industry: e.target.value })}
                className="flex-1 sm:flex-none sm:w-64 px-4 py-2.5 bg-white dark:bg-royal-800/60 border border-purple-400 dark:border-purple-500/50 rounded-lg text-base text-royal-700 dark:text-white focus:outline-none focus:border-purple-500 transition-colors"
              >
                <option value="">All Industries</option>
                {industries.filter(i => i.industryname !== 'All Business').map((i) => (
                  <option key={i.industrytypeid} value={i.industrytypeid}>{i.industryname}</option>
                ))}
              </select>
              {activeIndustry && (
                <button onClick={() => updateParams({ industry: '' })} className="text-purple-500 dark:text-purple-400 text-base hover:text-purple-400 dark:hover:text-purple-300 transition-colors">
                  Clear
                </button>
              )}
            </div>
          )}

          {/* Sidebar + grid layout — tops aligned */}
          <div className="flex gap-6">

            {/* Mobile sidebar overlay */}
            {sidebarOpen && (
              <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setSidebarOpen(false)} />
            )}

            {/* ── Sidebar ──────────────────────────────────
                FIX: On mobile (closed), hide entirely with `hidden`
                so it can't paint a sliver. On desktop, always show
                via `lg:block`. When open on mobile, render as a fixed
                left panel anchored with `left-0`. The previous bug:
                `-translate-x-full` only shifts by the sidebar's own
                width (16rem), and because the element was inside a
                flex parent with no explicit `left-0`, paint/rounding
                issues left a thin visible strip.
            ──────────────────────────────────────────────── */}
            <aside
              className={`
                ${sidebarOpen ? 'block' : 'hidden'} lg:block
                fixed lg:relative
                top-14 lg:top-auto
                left-0 lg:left-auto
                z-40 lg:z-auto
                w-64 h-[calc(100vh-3.5rem)] lg:h-auto lg:self-start
                overflow-y-auto
                bg-white dark:bg-royal-900
                border border-royal-200 dark:border-royal-700/50
                rounded-none lg:rounded-xl
                p-3 shrink-0
                shadow-2xl lg:shadow-none
              `}
            >
              {/* Mobile-only close button */}
              <div className="lg:hidden flex items-center justify-between mb-2 px-1">
                <span className="text-sm font-semibold uppercase tracking-wider text-gold-dark dark:text-gold">Browse</span>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="p-1.5 rounded-lg text-royal-500 dark:text-royal-300 hover:bg-royal-100 dark:hover:bg-royal-800 transition-colors"
                  aria-label="Close categories"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* All eCards */}
              <SidebarItem
                icon="🃏"
                label="All Moonbeams"
                active={!activeCategory && !showSpecialAccess}
                onClick={() => { selectCategory(''); setShowSpecialAccess(false); }}
              />

              {/* Section label */}
              <p className="px-3 pt-4 pb-2 text-sm font-semibold uppercase tracking-wider text-gold-dark dark:text-gold">
                Categories
              </p>

              {/* Category items — flat list, no collapsible */}
              {personalCategories.length > 0 ? (
                <div className="space-y-0.5">
                  {personalCategories.map((c) => (
                    <SidebarItem
                      key={c.categoryid}
                      icon={getCategoryIcon(c.categoryname)}
                      label={c.categoryname}
                      count={c.templatecount || undefined}
                      active={activeCategory === String(c.categoryid) && !showSpecialAccess}
                      onClick={() => { selectCategory(c.categoryid); setShowSpecialAccess(false); }}
                    />
                  ))}
                </div>
              ) : (
                <p className="px-3 py-2 text-xs text-royal-400 italic">Loading…</p>
              )}
            </aside>

            {/* ── Main content ───────────────────────────── */}
            <div className="flex-1 min-w-0 pb-10">

              {/* Vintage Search Bar */}
              {!showSpecialAccess && (
                <div className="mb-6 max-w-md">
                  <VintageSearch
                    value={searchQuery}
                    onChange={setSearchQuery}
                    placeholder="Search the collection…"
                  />
                </div>
              )}

              {/* Special Access Section */}
              {showSpecialAccess && (
                <div className="mb-8">
                  <div className="flex items-center gap-3 mb-4">
                    <h2 className="text-gold-dark dark:text-gold font-display text-xl">Special Access Moonbeams</h2>
                    <div className="h-px flex-1 bg-gradient-to-r from-gold/30 to-transparent"></div>
                  </div>

                  {specialAccessLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="w-8 h-8 border-2 border-royal-300 dark:border-royal-600 border-t-gold rounded-full animate-spin" />
                    </div>
                  ) : specialAccessTemplates.length === 0 ? (
                    <div className="bg-white dark:bg-royal-800/40 border border-royal-200/40 dark:border-royal-600 rounded-xl p-8 text-center mb-6">
                      <div className="text-3xl mb-3">✨</div>
                      <h3 className="text-royal-800 dark:text-white font-medium mb-2">No Special Access Right Now</h3>
                      <p className="text-royal-500 dark:text-royal-200 text-sm max-w-md mx-auto leading-relaxed">
                        From time to time, we grant special access to exclusive eCard collections
                        based on qualifying actions — like being one of our top senders or
                        participating in seasonal events. Keep an eye on your email for
                        invitations to unlock special templates!
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Info banner — check email for details */}
                      <div className="mb-6 p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-500/30 rounded-xl">
                        <div className="flex gap-3 items-start">
                          <span className="text-lg flex-shrink-0">📧</span>
                          <div>
                            <p className="text-royal-700 dark:text-royal-100 text-sm leading-relaxed">
                              You've been granted special access to exclusive eCard templates! Check your email for more details about each collection.
                            </p>
                            <p className="text-royal-500 dark:text-royal-300 text-xs mt-1.5 leading-relaxed">
                              Don't see the email? Check your spam or trash folder, and move it to your primary inbox so you receive future updates.
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Grouped by accessname */}
                      <div className="space-y-8">
                        {specialAccessGroups.map((group) => (
                          <div key={group.name}>
                            {/* Group header */}
                            <div className="flex items-center gap-3 mb-4">
                              <div className="flex items-center gap-2">
                                <span className="text-purple-500 dark:text-purple-400 text-base">✦</span>
                                <h3 className="text-royal-800 dark:text-white font-display text-lg">{group.name}</h3>
                              </div>
                              {group.expiresat && (
                                <span className="text-royal-400 dark:text-royal-300 text-xs whitespace-nowrap">
                                  Expires {new Date(group.expiresat).toLocaleDateString()}
                                </span>
                              )}
                              <div className="h-px flex-1 bg-gradient-to-r from-purple-300/30 dark:from-purple-500/20 to-transparent"></div>
                              <span className="text-royal-400 dark:text-royal-300 text-xs whitespace-nowrap">
                                {group.templates.length} eCard{group.templates.length !== 1 ? 's' : ''}
                              </span>
                            </div>

                            {/* Cards grid */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-7">
                              {group.templates.map((t) => (
                                <div key={t.templateid} className="relative">
                                  <MoonbeamCard template={t} />
                                  <div className="absolute top-2 right-2">
                                    <span className="px-2 py-1 bg-purple-900/80 text-purple-300 text-xs font-bold rounded-full border border-purple-500/30">
                                      Special Access
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Free tier info banner */}
              {!showSpecialAccess && isFreeUser && !isPaidUser && activeFreeFilter !== '1' && (
                <div className="mb-6 p-4 bg-white dark:bg-royal-800/40 border border-royal-200/40 dark:border-royal-600 rounded-xl">
                  <p className="text-royal-600 dark:text-royal-200 text-sm text-center">
                    You can preview any eCard. Cards marked <span className="text-green-600 dark:text-green-400 font-medium">Free</span> can be sent with your current plan.{' '}
                    <a href="/pricing" className="text-royal-900 dark:text-gold hover:text-royal-700 dark:hover:text-gold-light font-semibold underline transition-colors">Upgrade</a> to send all Moonbeams.
                  </p>
                </div>
              )}

              {/* Results count */}
              {!showSpecialAccess && !loading && (
                <p className="text-royal-500 dark:text-royal-300 text-sm mb-4">
                  {pagination.total} template{pagination.total !== 1 ? 's' : ''}
                  {activeCategoryName && ` in ${activeCategoryName}`}
                  {activeFreeFilter === '1' && ' (free only)'}
                  {debouncedSearch && ` matching "${debouncedSearch}"`}
                  {activeIndustry && showBusinessTab && activeTab === 'business' && ` for ${selectedIndustryName}`}
                </p>
              )}

              {/* Templates grid */}
              {!showSpecialAccess && (
                <>
                  {loading ? (
                    <div className="flex items-center justify-center py-20">
                      <div className="w-8 h-8 border-2 border-royal-300 dark:border-royal-600 border-t-gold rounded-full animate-spin" />
                    </div>
                  ) : filteredTemplates.length === 0 ? (
                    <div className="text-center py-16">
                      <div className="text-4xl mb-4">✨</div>
                      <p className="text-royal-700 dark:text-royal-100 text-lg mb-2">
                        {debouncedSearch ? 'No eCards match your search' : 'No templates found'}
                      </p>
                      <p className="text-royal-500 dark:text-royal-200 text-base italic">
                        {debouncedSearch
                          ? 'Try a different enchantment…'
                          : activeFreeFilter === '1'
                            ? 'No free eCards match your current filters. Try showing all eCards.'
                            : activeTab === 'business' && activeIndustry
                              ? 'Try selecting a different industry or clear the filter.'
                              : 'Check back soon for new templates!'}
                      </p>
                      {(activeCategory || activeIndustry || activeFreeFilter || debouncedSearch) && (
                        <button
                          onClick={() => { updateParams({ category: '', industry: '', free: '' }); setSearchQuery(''); }}
                          className="mt-6 text-gold-dark dark:text-gold hover:text-gold dark:hover:text-gold-light text-base transition-colors"
                        >
                          Clear all filters
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-7">
                      {filteredTemplates.map((t) => (
                        <div key={t.templateid} className="relative">
                          <MoonbeamCard template={t} />
                          {/* Admin Only badge — only visible to admins */}
                          {isAdmin && t.isadmin && (
                            <div className="absolute top-2 left-2">
                              <span className="px-2 py-1 bg-orange-900/80 text-orange-300 text-xs font-bold rounded-full border border-orange-500/30">
                                Admin Only
                              </span>
                            </div>
                          )}
                          {isFreeUser && !isPaidUser && t.isfree && (
                            <div className="absolute top-2 right-2">
                              <span className="px-2 py-1 bg-green-100 dark:bg-green-900/80 text-green-700 dark:text-green-400 text-xs font-bold rounded-full border border-green-300 dark:border-green-500/30">
                                Free
                              </span>
                            </div>
                          )}
                          {showBusinessTab && t.isbusiness && t.industryTypes && t.industryTypes.length > 0 && (
                            <div className="absolute top-2 left-2 flex flex-wrap gap-1">
                              {t.industryTypes.slice(0, 2).map((it) => (
                                <span key={it.industrytypeid} className="px-1.5 py-0.5 bg-purple-900/80 text-purple-300 text-sm rounded">
                                  {it.industryname}
                                </span>
                              ))}
                              {t.industryTypes.length > 2 && (
                                <span className="px-1.5 py-0.5 bg-purple-900/80 text-purple-300 text-sm rounded">
                                  +{t.industryTypes.length - 2}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  <Pagination page={pagination.page} totalPages={pagination.totalPages} onPageChange={handlePageChange} />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}